/**
 * Quick Add for "You may also like" cart recommendations. Shared between
 * the Cart Drawer and the full Cart Page -- the same snippet
 * (snippets/soluscent-cart-recommendations.liquid) renders in both, and
 * this script tells them apart per click via `button.closest('cart-drawer')`,
 * matching assets/soluscent-cart-gift-upgrade.js's own drawer/page split.
 *
 * Reuses the theme's existing add-to-cart/render pipeline instead of a
 * parallel one:
 * - `fetchConfig()` / `routes.cart_add_url` (assets/global.js) -- the same
 *   request assets/product-form.js makes for a normal Add to Cart.
 * - Drawer mode: `cart-drawer`'s own `getSectionsToRender()` /
 *   `renderContents()` (assets/cart-drawer.js) -- exactly what
 *   assets/product-form.js calls after a normal Add to Cart, so this is
 *   published as source: 'product-form' and needs no changes to
 *   assets/cart.js's cartUpdate subscriber for the drawer case.
 * - Page mode: the same main-cart-items / main-cart-footer / cart-icon-
 *   bubble / cart-live-region-text section ids and whole-section-swap
 *   patching assets/soluscent-cart-gift-upgrade.js already uses for its
 *   own page-mode Upgrade button, so Delivery Protection, the free-
 *   shipping bar, the Gift Upgrade module and these recommendations
 *   themselves (all rendered inside main-cart-footer) all pick up the
 *   post-add state together. Published as its own source
 *   ('soluscent-quick-add-page'), paired with a matching skip-condition
 *   added to assets/cart.js's CartItems subscriber, so that subscriber's
 *   own ?section_id=main-cart-items re-fetch doesn't run a second time on
 *   top of the patch this script just did.
 *
 * This add-to-cart response is the same single-line-item shape every
 * normal product-form.js Add to Cart already returns (not the full cart
 * object /cart.js, /cart/change.js and /cart/update.js return) -- Delivery
 * Protection's own reconcile() (assets/soluscent-shipping-protection.js)
 * already falls back to one /cart.js read whenever it sees that shape, on
 * every Add to Cart site-wide, not just here. That behaviour is
 * pre-existing and unrelated to Quick Add's own request pattern, so it is
 * left alone rather than worked around.
 */
(function () {
  if (window.SoluscentCartRecommendationsQuickAdd) return;
  window.SoluscentCartRecommendationsQuickAdd = true;

  // Long enough to read "Added" before the recommendation grid
  // (necessarily) reshuffles under it -- the just-added product no longer
  // qualifies as a recommendation once it's in the cart, so this exact
  // button will not exist once the section patch below lands.
  var SUCCESS_HOLD_MS = 650;

  function getMode(button) {
    return button.closest('cart-drawer') ? 'drawer' : 'page';
  }

  function setBusy(button, busy) {
    button.disabled = busy;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
    button.classList.toggle('is-loading', busy);
    var spinner = button.querySelector('.soluscent-quick-add__spinner');
    if (spinner) spinner.classList.toggle('hidden', !busy);
  }

  function showSuccess(button) {
    button.classList.remove('is-loading');
    button.classList.add('is-added');
    var spinner = button.querySelector('.soluscent-quick-add__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  function getSectionsToRequest(mode) {
    if (mode === 'drawer') {
      var cartDrawer = document.querySelector('cart-drawer');
      return cartDrawer ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; }) : [];
    }
    var ids = [];
    var itemsEl = document.getElementById('main-cart-items');
    var footerEl = document.getElementById('main-cart-footer');
    if (itemsEl && itemsEl.dataset.id) ids.push(itemsEl.dataset.id);
    if (footerEl && footerEl.dataset.id) ids.push(footerEl.dataset.id);
    ids.push('cart-icon-bubble');
    ids.push('cart-live-region-text');
    return ids;
  }

  function applySections(mode, state) {
    if (mode === 'drawer') {
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.renderContents(state);
      return;
    }

    if (!state.sections) return;

    var itemsEl = document.getElementById('main-cart-items');
    if (itemsEl && itemsEl.dataset.id && state.sections[itemsEl.dataset.id]) {
      var itemsDoc = new DOMParser().parseFromString(state.sections[itemsEl.dataset.id], 'text/html');
      var itemsSource = itemsDoc.querySelector('.js-contents');
      var itemsTarget = itemsEl.querySelector('.js-contents');
      if (itemsSource && itemsTarget) itemsTarget.innerHTML = itemsSource.innerHTML;
      itemsEl.closest('cart-items') &&
        itemsEl.closest('cart-items').classList.toggle('is-empty', false);
    }

    var footerEl = document.getElementById('main-cart-footer');
    if (footerEl && footerEl.dataset.id && state.sections[footerEl.dataset.id]) {
      var footerDoc = new DOMParser().parseFromString(state.sections[footerEl.dataset.id], 'text/html');
      var footerSource = footerDoc.getElementById('main-cart-footer');
      if (footerSource) footerEl.innerHTML = footerSource.innerHTML;
      footerEl.classList.toggle('is-empty', false);
    }

    var bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl && state.sections['cart-icon-bubble']) {
      var bubbleDoc = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html');
      var bubbleSource = bubbleDoc.querySelector('.shopify-section');
      if (bubbleSource) bubbleEl.innerHTML = bubbleSource.innerHTML;
    }

    var liveTextEl = document.getElementById('cart-live-region-text');
    if (liveTextEl && state.sections['cart-live-region-text']) {
      var liveDoc = new DOMParser().parseFromString(state.sections['cart-live-region-text'], 'text/html');
      var liveSource = liveDoc.querySelector('.shopify-section');
      if (liveSource) liveTextEl.innerHTML = liveSource.innerHTML;
    }
  }

  function errorsElementFor(mode) {
    return mode === 'drawer'
      ? document.getElementById('CartDrawer-CartErrors')
      : document.getElementById('cart-errors');
  }

  function quickAdd(button) {
    if (button.disabled) return;

    var variantId = button.dataset.variantId;
    if (!variantId) return;

    var mode = getMode(button);
    var sectionIds = getSectionsToRequest(mode);
    var sectionsUrl = window.location.pathname;

    setBusy(button, true);

    fetch(
      window.routes.cart_add_url,
      Object.assign({}, fetchConfig(), {
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds,
          sections_url: sectionsUrl,
        }),
      })
    )
      .then(function (response) {
        return response.json();
      })
      .then(function (addResponse) {
        if (addResponse.status) {
          throw new Error(addResponse.description || (window.cartStrings && window.cartStrings.error));
        }

        showSuccess(button);

        window.setTimeout(function () {
          applySections(mode, addResponse);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: mode === 'drawer' ? 'product-form' : 'soluscent-quick-add-page',
            productVariantId: variantId,
            cartData: addResponse,
          });
        }, SUCCESS_HOLD_MS);
      })
      .catch(function (e) {
        console.error(e);
        setBusy(button, false);
        button.classList.remove('is-added');
        var errors = errorsElementFor(mode);
        if (errors) errors.textContent = (e && e.message) || (window.cartStrings && window.cartStrings.error) || '';
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-soluscent-quick-add-submit]');
    if (button) quickAdd(button);
  });
})();
